import message from 'antd/es/message';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { TOKEN } from 'features/auth/constants';
import { getAuthToken, handleRefreshToken } from 'features/auth/helpers';
import { HttpClientProps, TQueryConfig, TQueryError } from 'lib/axios/types';
import { startLoadingSpinner, stopLoadingSpinner } from 'utils/loadingSpinner';
import { ObjectType } from 'utils/types';

axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig & TQueryConfig) => {
    const { disableSpinner } = config;
    if (!disableSpinner) {
      startLoadingSpinner();
    }
    return config;
  },
  (error: AxiosError) => {
    stopLoadingSpinner();
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    stopLoadingSpinner();
    return response.data;
  },
  async (error: AxiosError) => {
    const { response, code, config } = error;
    const originalRequest = config as
      | (AxiosRequestConfig & {
          _retry?: boolean;
        })
      | null;
    const { manuallyHandlingErrorMsg } = config as AxiosError['config'] &
      TQueryConfig;
    stopLoadingSpinner();

    const isTokenEndpoint = Boolean(
      originalRequest?.url?.includes('/protocol/openid-connect/token')
    );
    const isUnauthorized = response?.status === 401 || response?.status === 403;
    if (
      isUnauthorized &&
      !isTokenEndpoint &&
      originalRequest &&
      !originalRequest._retry &&
      getAuthToken(TOKEN.REFRESH_TOKEN)
    ) {
      originalRequest._retry = true;
      return handleRefreshToken(originalRequest);
    }

    if (response?.status === 408 || code === 'ECONNABORTED') {
      message.error('Request timed out.');
    }

    !manuallyHandlingErrorMsg &&
      // @ts-ignore
      message.error(response?.data?.message ?? 'Unknown error occurred.');

    return Promise.reject(manuallyHandlingErrorMsg ? response?.data : error);
  }
);

export const axiosBaseQuery =
  ({ baseUrl } = { baseUrl: '' }) =>
  async (config: HttpClientProps & TQueryConfig) => {
    const {
      url,
      method,
      data,
      params,
      customHeader,
      manuallyHandlingErrorMsg,
      disableSpinner,
    } = config;

    const accessToken = getAuthToken(TOKEN.ACCESS_TOKEN);

    const axiosConfig = {
      url: baseUrl + url,
      method,
      headers: {
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        ...customHeader,
      },
      ...(params && { params }),
      ...(data && { data }),
      manuallyHandlingErrorMsg,
      disableSpinner,
    } as AxiosRequestConfig<ObjectType> & TQueryConfig;

    try {
      const result = await axios(axiosConfig);
      return {
        data: result,
      };
    } catch (err: any) {
      return {
        data: null,
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message || err.error,
          success: err.success || false,
        } as TQueryError,
      };
    }
  };
